import { convertToOccasions } from './lib/occasions-adapter';
import { create, valueOf } from 'microstates';
import { fileFromSVGElement, textToElement } from './lib/svg-utils';
import HistoryState from './states/history-state';
import ThemeState from './states/theme';
import PhotoState from './states/photo';
import RouterState from './states/router';
import {
  saveNote,
  updateNote,
  createStorageParams,
  uploadImage,
  getColors,
  getFonts
} from './api';
import AppState from './states/app-state';
import { getNote, getNoteGraphic } from './api';
import { writable } from 'svelte/store';
import { tick } from 'svelte';
import { stateFromSvg } from './lib/state-from-svg';
import WebFont from 'webfontloader';
import { create as createPhraseFilter } from 'phrase-filter';
import {
  messagesDoNotMatch,
  attemptToSyncSVGToMessage,
  logMessagesMismatch,
  logIfDOMMismatch,
  logIfFormDataMismatch,
  logIfServerMismatch
} from './lib/assert-utils';

export let phraseFilter;
export let store;
export let history;
export let theme;
export let photo;
export let router;

export const destroy = () => {
  store = null;
  history = null;
  theme = null;
  photo = null;
  router = null;
}

export const constructAppState = async ({
  occasions,
  messages,
  text,
  headers,
  noteResourceId,
  endpoint,
  format,
  logoSrc,
  noteInsertGifSrc,
  spaceChar,
  center,
  lineLengths,
  withHyphenation,
  back,
  forward,
  backText,
  forwardText,
  noticeError,
  bannedPhrases,
  terms
}) => {

  theme = writable(create(ThemeState));
  photo = writable(create(PhotoState));
  store = writable();
  history = writable(create(HistoryState));
  router = writable(create(RouterState).to('edit'));

  phraseFilter = createPhraseFilter(bannedPhrases);

  /*
   * TODO: next time we touch the occasions/messages in shopify
   * we should update the structure so we don't have to do this
   *
   * Adapt the current format of occasions and messages
   * passed in from shopify to something useful
   */
  const occ = convertToOccasions(occasions, messages);

  let $photo;
  let $theme;
  const subscriptions = {
    unsubscribe() {
      this.subs.forEach(unsubscribe => unsubscribe());
    },
    subs: [
      photo.subscribe($ => $photo = $),
      theme.subscribe($ => $theme = $)
    ]
  }

  photo.set($photo.isHoveringPhoto.set(false));

  theme.set($theme.noteInsertGifSrc.set(noteInsertGifSrc).logoSrc.set(logoSrc));

  const textColors = await getColors({ endpoint, headers });
  const [textColor] = textColors;

  const alphabetically = (key) => (objA, objB) => {
    const valueA = objA[key].toLowerCase();
    const valueB = objB[key].toLowerCase();

    if (valueA < valueB) {
      return -1;
    }

    if (valueA > valueB) {
      return 1;
    }

    return 0;
  };

  const fontResp = await getFonts({ endpoint, headers });
  const fontFaces = fontResp.sort(alphabetically('family'));
  const fontFace = fontFaces.find((font) => { return font.family === 'Nothing You Could Do'}) || fontFaces[0];

  const fontFamilies = fontFaces.map((fontFace) => {
    return fontFace.family;
  });

  WebFont.load({
    google: {
      families: fontFamilies
    }
  });

  let initialAppState = create(AppState, {
    noticeError,
    text: { content: text },
    isEditing: true,
    isHoveringText: false,
    headers,
    noteResourceId,
    endpoint,
    fontFace,
    fontFaces,
    format,
    spaceChar,
    center,
    lineLengths,
    textColors,
    terms,
    textColor,
    withHyphenation,
    externalActions: {
      backAction: back,
      forwardAction: forward,
      backText,
      forwardText
    }
  }).setOccasions(occ);

  const errorHandler = (error) => {
    /* if there are multiple errors, only show one */
    if (initialAppState.hasErrors) { return; }

    initialAppState = initialAppState.addError(error);

    return Error(error);
  }

  let didError;

  if (noteResourceId) {
    /*
     *  Fetch the message and the svg of the existing note.
     *  Failure to retrieve either of these renders the
     *  editor useless so we show an error in place of
     *  the editor.
     */
    const notePartPromises = [
      getNote({ endpoint, headers, noteResourceId }),
      getNoteGraphic({ endpoint, headers, noteResourceId })
    ].map(p => p.catch(errorHandler));

    const parts = await Promise.all(notePartPromises);
    didError = (parts.map(r => (r instanceof Error)).indexOf(true) >= 0);

    /*
     * In case of error, these defaults allow the app
     * to boot up and display a nice error with the
     * Notifications component.
     */
    let message = '';
    let svg = '<svg></svg>';

    if (!didError) {
      message = parts[0].message;
      svg = parts[1];
    }

    initialAppState = initialAppState
      .text.setText(message)
      .svg.set(textToElement(svg).innerHTML)
      .shouldRenderFromServer.set(true);

    initialAppState = stateFromSvg(svg, initialAppState, { $photo, photo });
  }

  subscriptions.unsubscribe();

  return initialAppState;
}

export async function save() {
  let $store;
  const unsubscribe = store.subscribe($ => $store = $);
  let $photo;
  const unsubscribePhoto = photo.subscribe($ => $photo = $);

  try {
    if ($store.isSaving.state) { return; }

    let newState = $store.errors.removeAll().isSaving.set(true);
    store.set(newState);

    const noteResourceId = newState.noteResourceId.state;
    const headers = valueOf(newState.headers);

    const body = new FormData();
    const endpoint = newState.endpoint.state;

    /*
     * only try to upload photo if there is a blob.
     * if there's no blob, assume the photo is loaded
     * via url. this happens when a user edits a note
     * with an existing photo.
     */
    const shouldUploadPhoto = (newState.layout.state === 'photo')
                              && $photo.photoSrc.state
                              && $photo.blob.state;

    if (shouldUploadPhoto) {

      /* get signed params for cloudinary */
      const { params, url } = await createStorageParams({ endpoint, headers, body });


      /* upload to cloudinary */
      const formData  = new FormData();
      for (let p in params) {
        formData.append(p, params[p]);
      }

      const imageBlob = await $photo.blob.state;
      formData.append('file', imageBlob);

      const cloudinaryResp = await uploadImage({ url, body: formData });

      photo.set($photo.photoSrc.set(cloudinaryResp.url));

      /* we `await tick` because we need the svg to re-render
       * with the cloudinary url as the image source instead of
       * the base64 data uri. that, in turn triggers an update
       * to thie `state.printSvg`. refactoring this to be more
       * straightforward would be nice.
       */
      await tick();
    }

    let message = newState.getText;
    let svg = newState.printSvg.state;

    /*
     *  HACK
     *  every so often, we get a blank svg and send that to embla.
     *  this is an attempt to fix that.
     *  we're assuming that the user sees the correct version of the svg
     *  so we go back to the dom and use that svg
     *  instead of our `printSvg` we're keeping in the store.
     *  We retry 10 times or until it matches.
     */
    svg = await attemptToSyncSVGToMessage({ message, svg });

    await logIfDOMMismatch({ message, svg, log: (x) => $store.log(x) });

    body.append('message', message);
    body.append('graphic', fileFromSVGElement(svg));

    logIfFormDataMismatch({ body, log: (x) => $store.log(x) });

    const { id } = noteResourceId
      ? await updateNote({ endpoint, headers, body, id: noteResourceId })
      : await saveNote({ endpoint, headers, body });

    logIfServerMismatch({ message, svg, id, log: (x) => $store.log(x) });

    store.set(newState.noteResourceId.set(id).isSaving.set(false));
    return true;
  } catch (e) {
    store.set($store.errors.add(e).isSaving.set(false));
    throw e;
  } finally {
    unsubscribe();
    unsubscribePhoto();
  }
}
